import { PhotoCameraRounded, FileUpload } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { Box, Typography } from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { FC } from 'react';
import { getSecuritySensitiveRandomNumber } from '../../helpers';
import { useSnackbar } from 'notistack';

interface IManyImagesUpload {
  handleFileChange: (val: any[], file?: File[]) => void;
  isDisabled?: boolean;
  isRequired?: boolean;
  showSource?: boolean;
  handleUploadClick?: () => void;
  imageTitle?: string;
  images: string[];
  isLoading?: boolean;
  imageKey: string;
  fileType?: string;
  uploadText?: string;
  headingVariant?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  headingColor?: string;
  headingFontSize?: string;
  buttonSize?: string;
}

export const ManyImagesUpload: FC<IManyImagesUpload> = ({
  imageTitle,
  handleUploadClick,
  handleFileChange,
  images,
  isLoading,
  isDisabled = false,
  isRequired = false,
  showSource = true,
  imageKey,
  fileType,
  uploadText,
  headingVariant = 'h6',
  headingColor,
  headingFontSize,
  buttonSize
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles({ headingColor, headingFontSize });

  const createBase64Image = async (file: Blob) => {
    const reader = new FileReader();
    return new Promise(function (resolve, reject) {
      reader.onload = function (event) {
        resolve(event?.target?.result);
      };
      reader.readAsDataURL(file);
    });
  };
  const handleCapture = async (target: EventTarget & HTMLInputElement) => {
    if (target.files) {
      if (target.files.length !== 0) {
        const files = [];
        const base64Images: Array<{
          title: string;
          urlPath: string;
          contents: string;
          isUploaded: boolean;
          whenTaken: Date | string;
          servicePhotoId: string;
          file: File;
        }> = [];
        // Get fileList Object, convert to an array and get the values before they disappear
        // https://stackoverflow.com/questions/25333488/why-isnt-the-filelist-object-an-array
        const allFiles: File[] = Array.from(target.files);
        const fileLength = target.files.length;
        for (let i = 0; i < fileLength; i++) {
          const file = allFiles[i];
          let acceptedFileTypes: string[] = fileType.split(', ');
          let uploadedFileType = file.type.split?.('/')?.[1] ?? '';

          // quicktime movie
          if (uploadedFileType === 'quicktime') {
            uploadedFileType = 'mov';
            // plain text
          } else if (uploadedFileType === 'plain') {
            uploadedFileType = 'txt';
            // word doc
          } else if (uploadedFileType.includes('document')) {
            uploadedFileType = 'docx';
          }
          if (!acceptedFileTypes.includes(`.${uploadedFileType}`)) {
            return enqueueSnackbar(
              `Invalid file type${
                uploadedFileType ? `, ${uploadedFileType}` : ''
              }. Please upload one of these types, ${acceptedFileTypes.join(', ')}.`,
              {
                variant: 'error'
              }
            );
          }

          const base64 = (await createBase64Image(file)) as string;
          const newUrl = URL.createObjectURL(file);

          files.push(file);
          base64Images.push({
            title: file.name,
            urlPath: newUrl,
            contents: base64.split(',')[1],
            isUploaded: false,
            whenTaken: '',
            servicePhotoId: Math.floor(
              1000000 + getSecuritySensitiveRandomNumber() * 9000000
            ).toString(),
            file: file
          });
        }
        handleFileChange(base64Images, files);
      }
    }
  };

  return (
    <div>
      <Typography variant={headingVariant} className={classes.heading}>
        {imageTitle}
        {isRequired && '*'}
      </Typography>
      {isRequired && images.length === 0 && (
        <Typography variant="caption" className={classes.imageHelpText}>
          Uploading an image is required.
        </Typography>
      )}
      <input
        accept={fileType ? fileType : 'image/*'}
        disabled={isDisabled}
        className={classes.input}
        id={`icon-button-file-${imageKey || ''}`}
        type="file"
        multiple
        onChange={e => {
          handleCapture(e.target);
          // reset the input value so you can re-upload the same photo if you want to
          e.target.value = '';
        }}
      />
      <Box>
        <label htmlFor={`icon-button-file-${imageKey || ''}`}>
          <LoadingButton
            loading={isLoading}
            loadingPosition="start"
            onClick={handleUploadClick}
            disabled={isDisabled}
            startIcon={fileType ? <FileUpload /> : <PhotoCameraRounded />}
            size={(buttonSize as any) ?? 'large'}
            component={'span'}
            sx={{
              '&:hover': {
                backgroundColor: 'rgba(139, 198, 64, 0.1)'
              }
            }}
          >
            {isLoading ? 'Uploading...' : uploadText ?? 'Upload Image(s)'}
          </LoadingButton>
        </label>
      </Box>
    </div>
  );
};

const useStyles = makeStyles<Theme, { headingColor?: string; headingFontSize?: string }>(theme => ({
  heading: {
    marginBottom: theme.spacing(0.5),
    color: props => props.headingColor || 'inherit',
    fontSize: props => props.headingFontSize || undefined
  },
  input: {
    display: 'none'
  },
  imageHelpText: {
    color: theme.palette.error.main
  }
}));
